"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AsyncMessageCommunicator = void 0;
const tslib_1 = require("tslib");
const debug_1 = tslib_1.__importDefault(require("debug"));
const messages_1 = require("./messages");
/**
 * Handles communication with an asynchronous window source.
 */
class AsyncMessageCommunicator {
    constructor(debugNamespace = 'amc') {
        /** Map of actions to handler functions to handle the request. */
        this.handlers = new Map();
        /** Specifies the origin to use when targeting the message. */
        this.origin = '*';
        /** Map of pending requests that are currently waiting for a response. */
        this.pendingRequests = new Map();
        /** The window object that will be the target of the command and send methods. */
        this.targetWindow = null;
        this._nextId = 0;
        /**
         * Receives incoming window event messages and will handle the command/request/response as
         * necessary based on the configuration of this instance. Any handled message will prevent
         * default behavior and stop any future propigation. Any unhandled message will be ignored and
         * allowed to be handled by other handlers.
         * @arg {MessageEvent} event The event describing the incoming Message.
         */
        this._messageHandler = async (event) => {
            const commData = event.data;
            if (!(0, messages_1.isAsyncCommsObject)(commData)) {
                const origin = (0, messages_1.handleSyncRequest)(this.targetWindow, event);
                // origin is null if it wasn't a sync request
                if (origin === null) {
                    return;
                }
                // origin is an empty string if the window is invalid
                if (origin === '') {
                    this._debug('got sync request and cannot respond to sync request without a target window!', event.data.id);
                    return;
                }
                // otherwise we're all good.
                this.setOrigin(origin);
                event.preventDefault();
                event.stopPropagation();
                this._debug('got sync request and responded to window', this.origin);
                return;
            }
            const { type } = commData;
            if (type === 'command') {
                const { action, data } = commData;
                const handler = this.handlers.get(action);
                if (!handler) {
                    this._debug('unrecognized command', action, commData);
                    return;
                }
                event.preventDefault();
                event.stopPropagation();
                this._debug('handling command', action, commData);
                handler(data);
                return;
            }
            if (type === 'request') {
                const { action, data, id } = commData;
                const handler = this.handlers.get(action);
                if (!handler) {
                    this._debug('unrecognized request', action, commData);
                    return;
                }
                event.preventDefault();
                event.stopPropagation();
                this._debug('handling request', action, id, data);
                const responseData = await handler(data);
                this._sendResponse(id, responseData);
                return;
            }
            if (type === 'response') {
                const { id, data } = commData;
                const resolve = this.pendingRequests.get(id);
                this.pendingRequests.delete(id);
                if (!resolve) {
                    this._debug('got response without pending request', id, commData);
                    return;
                }
                event.stopPropagation();
                event.preventDefault();
                this._debug('handling response', id, data);
                resolve(data);
                return;
            }
        };
        this._uniqueId = btoa(`${Math.ceil(Math.random() * 1000)}`);
        this._debug = (0, debug_1.default)(debugNamespace + `[${this._uniqueId}]`);
    }
    /**
     * Adds a new handler to the handler map for the given action.
     * @arg {string} action Name of the action the handler with handle.
     * @arg {Handler} handler Callback function to call when handling the action.
     */
    addHandler(action, handler) {
        this.addHandlers({ [action]: handler });
    }
    /**
     * Adds zero or more new handlers to the handler map for the given actions.
     * Action names are determined by the key.
     * @arg {Record<string, Handler>} handlers The handlers to add
     */
    addHandlers(handlers) {
        for (const [name, handler] of Object.entries(handlers)) {
            this.handlers.set(name, handler);
        }
    }
    /**
     * Expects a class that can create a new HandlerInstance class. After creation, this class will
     * receive a copy of this AsyncMessageCommunicator via the setAsyncMessageCommunicator method.
     * @arg {{ new(): HandlerInstance }} cls A class object that will create a new
     * {@see HandlerInstance} or inherits from the HandlerInstance class.
     */
    addHandlerClass(cls) {
        const instance = new cls();
        this.addHandlerInstance(instance);
    }
    /**
     * Expects an already constructed class inheriting from the {@see HandlerInstance} class to
     * receive this AsyncMessageCommunicator instance and return handlers via the getHandlers method.
     * @arg {HandlerInstance} instance An instance of a class inheriting from HandlerInstance to
     * register handlers with.
     */
    addHandlerInstance(instance) {
        instance.setAsyncMessageCommunicator(this);
        this.addHandlers(instance.getHandlers());
    }
    /**
     * Attaches a handler to the "message" event on the provided window.
     * @arg {Window} window The window that receives the event handler.
     */
    attachListener(window) {
        this.detachListener(window);
        this._debug('attach event listener');
        window.addEventListener('message', this._messageHandler, { capture: true });
    }
    /**
     * Clears the currently set window target. The {@see command} and {@see send} methods will no
     * longer work until a new window target is set.
     */
    clearTargetWindow() {
        this._debug('clear target window');
        this.targetWindow = null;
    }
    /**
     * Sends a "fire and forget" command to the target window.
     * @arg {string} action The action to command the listener to perform.
     * @arg {unknown=} data Any additional data to be sent with the command.
     */
    command(action, data) {
        if (!this.targetWindow) {
            this._debug('unable to post a command without a target window!');
            throw new Error('Unable to send command without a target window!');
        }
        const localHandler = this.handlers.get(action);
        if (localHandler) {
            this._debug('action has a local command handler, using that instead of bridge', action);
            localHandler(data).catch(reason => {
                this._debug('local command threw an error', reason);
            });
            return;
        }
        const id = this._getNextId();
        this._debug('sending command', id, action, data);
        (0, messages_1.postAsyncCommand)(this.targetWindow, this.origin, id, action, data);
    }
    /**
     * Removes the event listener from the "message" event for the provided window.
     * @arg {Window} window The window that will have the event handler removed.
     */
    detachListener(window) {
        this._debug('detach event listener');
        window.removeEventListener('message', this._messageHandler, { capture: true });
    }
    /**
     * Removes zero or more handlers from the handler map.
     * @arg {string[]} names The names of the handlers to remove.
     */
    removeHandlers(...names) {
        for (const name of names) {
            this.handlers.delete(name);
        }
    }
    /**
     * Sends a request to the target window and waits for a response before resolving.
     * @arg {string} action The action to request the listener to perform.
     * @arg {unknown=} data Any additional data to be sent with the request.
     * @returns {Promise<unknown>} Returns a promise that will eventually resolve in to a response
     * from the target, if they're listening. Due to the nature of the messages, this will never
     * reject failure.
     */
    async send(action, data) {
        if (!this.targetWindow) {
            this._debug('unable to send a request without a target window!');
            throw new Error('Unable to send request without a target window!');
        }
        const localHandler = this.handlers.get(action);
        if (localHandler) {
            this._debug('action has a local request handler, using that instead of bridge', action);
            return await localHandler(data);
        }
        const id = this._getNextId();
        this._debug('sending request', id, action, data);
        return await (0, messages_1.sendAsyncRequest)(this.targetWindow, this.pendingRequests, this.origin, id, action, data);
    }
    /** Sets the target origin for postMessage calls. */
    setOrigin(origin) {
        this.origin = origin;
    }
    /** Sets the target window to use when calling postMessage. */
    setTargetWindow(window) {
        this._debug('set target window', window);
        this.targetWindow = window;
    }
    /** Gets the next unique id for sending commands and requests. */
    _getNextId() {
        const id = ++this._nextId;
        return `acm-${this._uniqueId}-${id}`;
    }
    /**
     * Sends an asynchronous response for the given request.
     * @arg {string} id The id of the request we are sending a response.
     * @arg {unknown=} data Any data to send along with the response.
     */
    _sendResponse(id, data) {
        if (!this.targetWindow) {
            this._debug('unable to send response without a target window!');
            return;
        }
        this._debug('sending response', id, data);
        (0, messages_1.sendAsyncResponse)(this.targetWindow, this.origin, id, data);
    }
}
exports.AsyncMessageCommunicator = AsyncMessageCommunicator;
//# sourceMappingURL=AsyncMessageCommunicator.js.map